import React, { useContext } from 'react';
import { BreakpointContext, LARGE } from '@core-ui/breakpoint-provider';
import { CSSObject, forBrands, Theme } from '@core-ui/react-stitch';
import { useLocalize } from '@core-ui/localization-provider';
// @ts-ignore
import { RailContext } from '@cat/rail';
import { SIZE_FACET_REGULAR_VARIANT } from '../../localization-tokens';
import SizeButton from '../SizeButton';
import { Size } from '../../../../../types';

type StyleProps = {
  style: {
    name: string;
    sizes: Size[];
  };
  styleKey: string;
  variant: string;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onSizeClick: (
    size: { [property: string]: any },
    style: { [property: string]: any }
  ) => void;
};

const getStyleLabelStyleForAthleta = (theme: Theme): CSSObject => ({
  color: theme.color.g3,
  letterSpacing: '0.01rem',
});

const setStyleLabelStyleForBananaRepublic = (): CSSObject => ({
  fontSize: '0.875rem',
  textAlign: 'center',
});

const getStyleLabelStyleForBananaRepublic = (theme: Theme): CSSObject => ({
  // color: theme.color.b1,
  color: '#000000',
  textTransform: 'capitalize',
  ...(forBrands(theme, {
    br: setStyleLabelStyleForBananaRepublic,
    brfs: setStyleLabelStyleForBananaRepublic,
  }) as CSSObject),
});

const getStyleLabelStyleForGap = (theme: Theme): CSSObject => ({
  color: theme.color.b1,
  textTransform: 'capitalize',
});

const getStyleLabelStyleForOldNavy = (theme: Theme): CSSObject => ({
  ...theme.brandFont,
  fontWeight: 'bold',
  color: theme.color.g2,
  backgroundColor: theme.color.gray05,
  fontSize: '0.875rem',
  textAlign: 'center',
  textTransform: 'uppercase',
  padding: '0.5em',
});

const horizontalLineTextAccent = (t: Theme): CSSObject => ({
  overflow: 'hidden',
  '&:before, &:after': {
    // backgroundColor: t.color.b2,
    backgroundColor: '#000000',
    content: '""',
    display: 'inline-block',
    height: '1px',
    position: 'relative',
    verticalAlign: 'middle',
    width: '50%',
  },
  '&:before': {
    right: '0.5em',
    marginLeft: '-50%',
  },
  '&:after': {
    left: '0.5em',
    marginRight: '-50%',
  },
});

const getStyleLabelStyleForBrands = (theme: Theme) =>
  forBrands(theme, {
    at: getStyleLabelStyleForAthleta,
    br: getStyleLabelStyleForBananaRepublic,
    brfs: getStyleLabelStyleForBananaRepublic,
    gap: getStyleLabelStyleForGap,
    gapfs: getStyleLabelStyleForGap,
    on: getStyleLabelStyleForOldNavy,
  });

const styleLabelStyleBRRedesign = (
  theme: Theme,
  styleKey: number
): CSSObject => ({
  ...(styleKey === 0 && {
    fontWeight: 'bold',
    textTransform: 'uppercase',
  }),
  ...(styleKey !== 0 && horizontalLineTextAccent(theme)),
  // color: theme.color.b2,
  color: '#000000',
});

const styleLabelStyle = (theme: Theme, styleKey: number): CSSObject => ({
  marginBottom: '0.5rem',
  padding: '1em 0.5em 0 0.5em',
  // @ts-ignore
  ...getStyleLabelStyleForBrands(theme),
  ...(forBrands(theme, {
    br: () => styleLabelStyleBRRedesign(theme, styleKey),
    brfs: () => styleLabelStyleBRRedesign(theme, styleKey),
  }) as CSSObject),
});

const getStyleStyleForAthleta = (theme: Theme): CSSObject => ({
  // @ts-ignore
  borderBottom: `1px solid ${theme.color.g6}`,
});

const getStyleStyleForBananaRepublic = (theme: Theme): CSSObject => ({
  borderBottom: `1px solid ${theme.color.g4}`,
});

const getStyleStyleForGap = (theme: Theme): CSSObject => ({
  borderBottom: `1px solid ${theme.color.g4}`,
});

const getStyleStyleForOldNavy = (theme: Theme): CSSObject => ({
  borderBottom: `1px solid ${theme.color.g5}`,
});

const getStyleStyleForBrands = (theme: Theme) =>
  forBrands(theme, {
    at: getStyleStyleForAthleta,
    br: getStyleStyleForBananaRepublic,
    brfs: getStyleStyleForBananaRepublic,
    gap: getStyleStyleForGap,
    gapfs: getStyleStyleForGap,
    on: getStyleStyleForOldNavy,
  });

const styleStyle = (theme: Theme): CSSObject => ({
  position: 'relative',
  '&:last-child': {
    borderBottomWidth: 0,
  },
  // @ts-ignore
  ...getStyleStyleForBrands(theme),
});

const sizeStyleBRRedesign = (): CSSObject => ({
  paddingLeft: 0,
  paddingRight: 0,
  flexBasis: '25%',
});

const sizeStyle = (theme: Theme): CSSObject => ({
  boxSizing: 'border-box',
  listStyleType: 'none',
  padding: '0 0.8em 1em 0',
  display: 'inline-block',
  ...(forBrands(theme, {
    br: sizeStyleBRRedesign,
    brfs: sizeStyleBRRedesign,
  }) as CSSObject),
});

const Style = (props: StyleProps) => {
  const { style, styleKey, variant, onSizeClick } = props;

  const { onFacetChange } = useContext(RailContext);
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  const { localize } = useLocalize();
  const isLarge = greaterOrEqualTo(LARGE);

  const setStyleSizesStyleForBananaRepublic = (
    numberOfButtons: number
  ): CSSObject => ({
    display: 'flex',
    flexWrap: 'wrap',
    textAlign: 'center',
    justifyContent: numberOfButtons >= 4 ? 'left' : 'center',
  });

  const styleSizesStyle = (
    theme: Theme,
    numberOfButtons: number
  ): CSSObject => ({
    display: 'block',
    padding: '0.5rem 0 0.5rem 0.5rem',
    position: 'relative',
    ...(isLarge
      ? {
          flexWrap: 'wrap',
          overflowX: 'auto',
          '&::after': {
            background: 'none',
          },
        }
      : {}),
    ...(forBrands(theme, {
      br: () => setStyleSizesStyleForBananaRepublic(numberOfButtons),
      brfs: () => setStyleSizesStyleForBananaRepublic(numberOfButtons),
    }) as CSSObject),
  });

  const getSearchFacetOptionValue = (
    includeVariantName: boolean,
    size: Size
  ) => {
    const variantName = includeVariantName ? `${variant} ` : '';
    return (
      size.tagDisplayLabel || `${variantName}${size.sfcName}: ${size.name}`
    );
  };

  const onClick = (size: Size) => {
    const includeVariantName =
      size?.variantName?.toUpperCase() !== 'OMNI' &&
      size?.variantName?.toUpperCase() !==
        localize(SIZE_FACET_REGULAR_VARIANT)?.toUpperCase();

    // TODO: Reveluate these properties when deprecating old contract
    const selectedFacet = {
      facetName: 'size',
      dimId: size.dimId,
      facetOption: size,
      id: size.id,
      isSelected: !size.selected,
      searchFacetOptionId: size.searchFacetOptionId,
      searchFacetOptionValue: getSearchFacetOptionValue(
        includeVariantName,
        size
      ),
      sfcId: size.sfcId,
    };

    onFacetChange && onFacetChange(selectedFacet);
    onSizeClick(size, style);
  };

  return (
    <div
      key={`style-${styleKey.toString()}`}
      css={(theme) => styleStyle(theme)}
    >
      <p css={(theme) => styleLabelStyle(theme, +styleKey)}>{style.name}</p>
      <ul css={(theme) => styleSizesStyle(theme, style.sizes.length)}>
        {style.sizes.map((size, sizeKey) => (
          <li
            key={`size-${styleKey.toString()}-${sizeKey.toString()}`}
            css={(theme) => sizeStyle(theme)}
          >
            <SizeButton
              applied={size.selected}
              description={size.description}
              name={size.name}
              onClick={() => onClick(size)}
              style={style.name}
              variant={variant}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Style;
